<script setup>
import { datadogLogs } from "@datadog/browser-logs";
import { computed, nextTick, onBeforeMount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import { debounceSearchWrapper, useGetEntities } from "@/components/program-manager/sessions/composable";
import SearchableSelect from "@/components/shared/select/SearchableSelect.vue";
import { isTenantManager } from "@/composables/authorization";
import { useTenantEntityManager } from "@/composables/useTenantManager.js";
import { getEntityById } from "@/services/utils.js";
import { getStoredTenantContextId, isAuthorizedTenant } from "@/store/utils/caiqStorageSync";

const store = useStore();
const route = useRoute();

const { getSelectedEntityView, setSelectedEntityView } = useTenantEntityManager();

const selectedEntity = ref({ ...getSelectedEntityView.value });

const ownerEntityId = computed(() => {
  const entities = store.getters.getEntityIdsForTenantManager;
  return entities?.map((id) => id)[0];
});

const ownerEntityList = computed(() => {
  const entities = isTenantManager() ? [] : store.getters.getEntityIdsForTenantManager;
  return entities;
});

const {
  execute: executeGetEntities,
  state: entitiesOptions,
  isLoading: entitiesOptionsLoading,
} = useGetEntities(
  { immediate: true, throwError: false, resetOnExecute: true },
  {
    extraParams: {
      entity_ids: ownerEntityList.value,
    },
  },
);

const fetchEntitiesOptions = debounceSearchWrapper(executeGetEntities, 250, {
  extraParams: {
    entity_ids: ownerEntityList.value,
  },
});

onBeforeMount(async () => {
  // localStorage is the cross-app source of truth — prefer it over stale sessionStorage.
  const localStorageTenantId = getStoredTenantContextId();

  const shouldTryLocalStorageTenant =
    localStorageTenantId &&
    localStorageTenantId !== selectedEntity.value?.id &&
    isAuthorizedTenant(store, localStorageTenantId);

  let resolvedEntity;

  if (shouldTryLocalStorageTenant) {
    resolvedEntity = await getEntityById(localStorageTenantId);
  }

  if (!resolvedEntity && !selectedEntity.value?.id) {
    resolvedEntity = await getEntityById(ownerEntityId.value);
  }

  if (resolvedEntity) {
    setSelectedEntityView(resolvedEntity);
    selectedEntity.value = { ...getSelectedEntityView.value };
  }
});

// Sync from store when changed externally (e.g., cross-tab storage event or full entity data arriving)
watch(getSelectedEntityView, (newStoreEntity) => {
  if (newStoreEntity?.id !== selectedEntity.value?.id) {
    selectedEntity.value = { ...newStoreEntity };
  }
});
const adjustWidth = () => {
  nextTick(() => {
    const rightPadding = 25;
    const selectedOptionSpan = document.querySelector(".entity-select .vs__selected");
    const selectedOptionBox = document.querySelector(
      ".entity-select .v-select .vs__dropdown-toggle .vs__selected-options",
    );
    if (selectedOptionSpan && selectedOptionBox) {
      selectedOptionBox.style.width = `${selectedOptionSpan.scrollWidth + rightPadding}px`;
    }
  });
};
watch(selectedEntity, async (newEntity) => {
  adjustWidth();
  setSelectedEntityView(newEntity);
  await store.dispatch("programManager/setAllCounts");

  const subcategory = route?.meta?.subcategory?.toLowerCase();
  const path = route?.path.toLowerCase();

  // Let view-more components handle their own data fetching with toggle state
  const isViewMoreRoute = route?.name?.includes("view-more") ?? false;

  if (!isViewMoreRoute) {
    try {
      switch (true) {
        case path.includes("events"):
          await store.dispatch("programManager/fetchEventsData");
          break;
        case path.includes("programs"):
          await store.dispatch("programManager/fetchProgramsData");
          break;
        case path.includes("sessions"):
          await store.dispatch("programManager/fetchProgramSessionsData");
          break;
        case path.includes("housing"):
          await store.dispatch("programManager/fetchHousingData");
          break;
        case path.includes("internships"):
          await store.dispatch("programManager/fetchInternshipsData");
          break;
        default:
          console.warn("Unhandled subcategory:", subcategory);
      }
    } catch (error) {
      datadogLogs.logger.error(`Failed to fetch data for subcategory ${subcategory}:`, error);
    }
  }
  // For view-more routes: Do nothing, let the child component handle data fetching
});

onMounted(() => {
  adjustWidth();
});
</script>

<template>
  <div v-if="ownerEntityList" class="entity-select">
    <SearchableSelect
      v-model="selectedEntity"
      class="min-w-[200px]"
      :clear-search-on-blur="() => false"
      :deselect-from-dropdown="true"
      :loading="entitiesOptionsLoading"
      :options="entitiesOptions.items"
      placeholder="Select Owner"
      label="name"
      :clearable="false"
      @search="fetchEntitiesOptions"
    >
    </SearchableSelect>
  </div>
</template>

<style>
.entity-select .v-select .vs__dropdown-toggle {
  border-style: none;
}
.entity-select .v-select .vs__dropdown-toggle .vs__selected-options {
  flex-wrap: nowrap;
  text-wrap: nowrap;
}
</style>
